import { useQuery } from "react-query";

import { useUser } from "@/providers/context";
import { getWatchlist, ItemType, WatchlistResponse } from "@/api";
import { useCallback, useMemo } from "react";
import { ITEM_TYPE_COLLECTION, ITEM_TYPE_VIDEO } from "@/const.ts";
import { useAppProperties } from "@/hooks/useAppProperties.tsx";

export const useWatchlist = () => {
  const { uid } = useUser();
  const { showWatchlist } = useAppProperties();
  const query = useQuery(
    ["watchlist", uid],
    () => {
      if (uid) {
        return getWatchlist({ user_id: uid });
      }
    },
    {
      enabled: showWatchlist,
    },
  );

  const watchlistData: WatchlistResponse | undefined = useMemo(() => {
    if (!query.data) {
      return query.data;
    }
    const { Watchlist } = { ...query.data };
    return { ...query.data, Watchlist: Watchlist.sort((a, b) => b.Id - a.Id) };
  }, [query.data]);

  const { collections, videos } = useMemo(() => {
    const videos = new Set<number>();
    const collections = new Set<number>();
    const watchlistItems = query.data?.Watchlist ?? [];
    for (const w of watchlistItems) {
      if (isNaN(Number(w.ItemId))) {
        continue; // skip unexpected item ids
      }
      if (w.ItemType == ITEM_TYPE_VIDEO) {
        videos.add(parseInt(w.ItemId));
      }
      if (w.ItemType == ITEM_TYPE_COLLECTION) {
        collections.add(parseInt(w.ItemId));
      }
    }

    return { videos, collections };
  }, [query.data]);

  const videoIds = useMemo(() => {
    return Array.from(videos.values());
  }, [videos]);
  const collectionIds = useMemo(() => {
    return Array.from(collections.values());
  }, [collections]);

  const isInWatchlist = useCallback(
    ({ id, type }: { id: number; type: ItemType }) => {
      if (type === ITEM_TYPE_VIDEO) {
        return videos.has(id);
      } else if (type === ITEM_TYPE_COLLECTION) {
        return collections.has(id);
      }
      return false;
    },
    [videos, collections],
  );

  return {
    ...query,
    data: watchlistData,
    isInWatchlist,
    videoIds: videoIds,
    collectionIds: collectionIds,
  };
};
